import { createContext, ReactNode, useContext, useEffect, useState } from "react";

import { useCurrentScreen } from "@auth0/auth0-acul-react";

import {
  BRISTOL_ORG_ID,
  FLEX_ORG_ID,
  HOTELS_ORG_ID,
  isBristolOrg,
  isHotelsLikeOrg,
} from "@/constants/organizations";
import { extractOrgId } from "@/utils/helpers/authContextUtils";
import { logger } from "@/utils/logger/logger";

// Org-specific favicons. Orgs not listed here fall back to thon-favicon.svg
// (see the effect below), replacing Auth0's own default favicon.
const ORG_FAVICONS: Record<string, string> = {
  [HOTELS_ORG_ID]: "hotels-favicon.svg",
  [BRISTOL_ORG_ID]: "bristol-favicon.svg",
  [FLEX_ORG_ID]: "flex-favicon.svg",
};

// Auth0 ACUL renders our JS inside its own hosted page (e.g. a custom
// domain like dev-auth.thon.no), not our index.html - so a root-relative
// favicon href resolves against Auth0's origin and 404s. import.meta.url
// always points at wherever this module was actually fetched from, which is
// the same origin nginx serves /icons/* from.
const ASSET_ORIGIN = (() => {
  try {
    return new URL(".", import.meta.url).origin;
  } catch {
    return "";
  }
})();

interface OrganizationContextType {
  orgId: string | undefined;
  isHotelsOrg: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [orgId, setOrgId] = useState<string | undefined>(undefined);
  const [isHotelsOrg, setIsHotelsOrg] = useState(false);

  // Get context from Auth0 SDK
  const screenOptions = useCurrentScreen();
  const organization = screenOptions?.organization;
  const screen = screenOptions?.screen;
  const transaction = screenOptions?.transaction;
  const untrustedData = screenOptions?.untrustedData;

  // Extract org ID on mount and whenever context changes
  useEffect(() => {
    const extractedOrgId = extractOrgId(organization?.id, screen, transaction, untrustedData);

    if (extractedOrgId) {
      const truncated = extractedOrgId.startsWith("org_")
        ? extractedOrgId.substring(0, 8) + "..."
        : extractedOrgId;
      logger.log("OrganizationContext: Extracted org ID", { orgId: truncated });
      setOrgId(extractedOrgId);
      setIsHotelsOrg(isHotelsLikeOrg(extractedOrgId));
    } else if (!orgId) {
      // Only log if we don't already have an org ID cached.
      logger.log("OrganizationContext: No org ID found in SDK context or URL yet");
    }
  }, [organization?.id, screen, transaction, untrustedData, orgId]);

  // Always set our own favicon, so every org's icon - including the
  // thon-favicon.svg default - is controlled from this one place instead of
  // relying on whatever Auth0's page ships with.
  useEffect(() => {
    // Lets CSS target Bristol where it diverges from the shared .hotels-theme
    // (e.g. primary button hover color) without touching every screen.
    if (isBristolOrg(orgId)) {
      document.documentElement.dataset.org = "bristol";
    } else {
      delete document.documentElement.dataset.org;
    }

    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!favicon) return;

    const filename = (orgId && ORG_FAVICONS[orgId]) || "thon-favicon.svg";
    favicon.href = `${ASSET_ORIGIN}/icons/${filename}`;
  }, [orgId]);

  const value: OrganizationContextType = {
    orgId,
    isHotelsOrg,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

/**
 * Hook to access organization context
 * Provides org ID that persists across screen transitions
 */
export function useOrganizationContext(): OrganizationContextType {
  const context = useContext(OrganizationContext);

  if (!context) {
    throw new Error("useOrganizationContext must be used within OrganizationProvider");
  }

  return context;
}

/**
 * Hook to get org ID with fallback to direct extraction
 * Useful for backward compatibility with components that don't use context
 */
export function useOrganizationId(
  screen?: any,
  transaction?: any,
  untrustedData?: any,
): string | undefined {
  const { orgId: contextOrgId } = useOrganizationContext();

  // Return cached org ID first, fallback to direct extraction
  if (contextOrgId) {
    return contextOrgId;
  }

  return extractOrgId(undefined, screen, transaction, untrustedData);
}
